import { CategoryOptionId } from '@business/entities/category_option';
import { FormId } from '@business/entities/form';
import { OrderProductId, ProposalDisplayValueEnum } from '@business/entities/order';
import { CategoryId, RangValue, TMEstimateFeeId, TMEstimateId } from '@business/entities/tm';
import { ServiceProviderId } from '@business/entities/tm/TmServiceProviderResponse';
import { EstimatorTempateId, EstimatorTemplateGroupId, EstimatorTemplateItemId } from '@business/entities/tm/TmTemplateEstimatorResponse';
import { formArray, formControl, FormDataType, formGroup } from '@core/utils/form';

export const defineEstimateGroupLineItemForm = () => {
  return formGroup({
    id: formControl<EstimatorTemplateItemId>(),
    productId: formControl<OrderProductId>({ placeholder: 'Product', label: 'Product', required: false }),
    homeOwnerDescription: formControl<string>({ placeholder: 'Home Owner Description', label: 'Home Owner Description' }),
    title: formControl<string>({ placeholder: 'Title', label: 'Title' }),
    providerId: formControl<ServiceProviderId>({ placeholder: 'Provider' }),
    criteriaFormula: formControl<string>({ placeholder: 'Criteria Formula', label: 'Criteria Formula' }),
    qtyFormula: formControl<string>({ placeholder: 'Quantity Formula', label: 'Quantity Formula' }),
    quantity: formControl<number>({ placeholder: 'Quantity', label: 'Quantity' }),
    manualQty: formControl<number>({ placeholder: '', required: false, label: 'Manual Qty' }),
    markupFactor: formControl<number>({ placeholder: 'Markup Factor', label: 'Markup Factor', required: false }),
    weProvide: formControl<boolean>({ placeholder: 'We Provide', initialValue: false }),
    laborPrice: formControl<number>({ placeholder: 'Labor Price', required: true }),
    materialPrice: formControl<number>({ placeholder: 'Material Price', label: 'Material Price', required: true }),
    subCategoryId: formControl<CategoryId>({ placeholder: 'Sub Category', label: 'Sub Category' }),
    subMenuOrder: formControl<number>({ placeholder: 'Sub Menu Order', label: 'Sub Menu Order', initialValue: 0 }),
    isCustom: formControl<boolean>({ initialValue: false }),
    customTitle: formControl<string>({ placeholder: 'Custom Title', label: 'Custom Title' }),
    note: formControl<string>({ placeholder: 'Note', label: 'Note' }),
    proposalDisplay: formControl<ProposalDisplayValueEnum>({ placeholder: 'Proposal Display', label: 'Proposal Display' }),
  });
};

export const defineEstimateItemGroupForm = () => {
  return formGroup({
    id: formControl<EstimatorTemplateGroupId>(),
    name: formControl<string>({ required: true, label: 'Name' }),
    optionId: formControl<CategoryOptionId>({ label: 'Option', placeholder: 'Option' }),
    categoryId: formControl<CategoryId>({ label: 'Category' }),
    providerId: formControl<ServiceProviderId>({ label: 'Vendor' }),
    loPrice: formControl<number>({ required: true, label: 'Low Price' }),
    hiPrice: formControl<number>({ required: true, label: 'High Price' }),
    range: formControl<RangValue>({ required: true, label: 'Range', initialValue: RangValue.total }),
    // feeIds: formControl<TMEstimatorTemplateFeeId[]>({ label: 'Fee' }),
    markupFactor: formControl<number>({ required: false, label: 'Markup Factor %', initialValue: 0 }),
    items: formArray(() => defineEstimateGroupLineItemForm()),
  });
};

export const defineEstimateFeeForm = () => {
  return formGroup({
    id: formControl<EstimatorTempateId>(),
    feeId: formControl<TMEstimateFeeId>(),
    name: formControl<string>({ placeholder: 'Title', label: 'Title' }),
    amount: formControl<number>({ placeholder: 'Amount' }),
    adjustmentAmount: formControl<number>({ placeholder: 'Adjustment Amount' }),
    taxable: formControl<boolean>({ placeholder: 'Taxable', initialValue: false }),
    isChecked: formControl<boolean>({ initialValue: false }),
    formula: formControl<string>({ placeholder: 'Formula', label: 'Formula' }),
  });
};

export const defineEstimateForm = () => {
  return formGroup({
    id: formControl<EstimatorTempateId>(),
    templateId: formControl<EstimatorTempateId>(),
    jobTypeId: formControl<string>({ placeholder: 'Job Type', label: 'Job Type' }),
    title: formControl<string>({ placeholder: 'Title', label: 'Title' }),
    formIds: formControl<FormId[]>({ label: 'Form' }),
    groups: formArray(() => defineEstimateItemGroupForm()),
    fees: formArray(() => defineEstimateFeeForm()),
  });
};

export type EstimateItemFormData = FormDataType<typeof defineEstimateForm>;
export type EstimateItemFormType = ReturnType<typeof defineEstimateForm>;

export type EstimateFeeForm = FormDataType<typeof defineEstimateFeeForm>;
export type EstimateFeeFormType = ReturnType<typeof defineEstimateFeeForm>;

export type EstimateItemGroupForm = ReturnType<typeof defineEstimateItemGroupForm>;
export type EstimateItemGroupData = FormDataType<typeof defineEstimateItemGroupForm>;

export type EstimateGroupLineItemForm = ReturnType<typeof defineEstimateGroupLineItemForm>;
export type EstimateGroupLineItemData = FormDataType<typeof defineEstimateGroupLineItemForm>;
